import { TouchControls } from './touch-controls';

export class InputManager {
  private keys = new Set<string>();
  private mouseButtons = new Set<number>();
  private mouseDX = 0;
  private mouseDY = 0;
  private scrollDelta = 0;
  private _isPointerLocked = false;
  private canvas: HTMLCanvasElement;
  touch: TouchControls;
  private _isMobile: boolean;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.touch = new TouchControls(canvas);
    this._isMobile = TouchControls.isMobile();

    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('keyup', this.onKeyUp.bind(this));
    canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    canvas.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
    document.addEventListener('pointerlockchange', this.onPointerLockChange.bind(this));

    if (this._isMobile) {
      this.touch.enable();
    }
  }

  get isMobile(): boolean {
    return this._isMobile;
  }

  private onKeyDown(e: KeyboardEvent): void {
    this.keys.add(e.code);
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.code);
  }

  private onMouseDown(e: MouseEvent): void {
    this.mouseButtons.add(e.button);
    if (!this._isPointerLocked && !this._isMobile) {
      this.canvas.requestPointerLock();
    }
  }

  private onMouseUp(e: MouseEvent): void {
    this.mouseButtons.delete(e.button);
  }

  private onMouseMove(e: MouseEvent): void {
    if (this._isPointerLocked) {
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.scrollDelta += Math.sign(e.deltaY);
  }

  private onPointerLockChange(): void {
    this._isPointerLocked = document.pointerLockElement === this.canvas;
  }

  isKeyDown(code: string): boolean {
    return this.keys.has(code) || this.touch.virtualKeys.has(code);
  }

  isMouseDown(button: number): boolean {
    return this.mouseButtons.has(button) || this.touch.virtualMouse.has(button);
  }

  getMouseDelta(): [number, number] {
    let dx = this.mouseDX;
    let dy = this.mouseDY;
    this.mouseDX = 0;
    this.mouseDY = 0;

    // Add touch look delta
    if (this.touch.isEnabled()) {
      const [tdx, tdy] = this.touch.consumeLookDelta();
      dx += tdx;
      dy += tdy;
    }

    return [dx, dy];
  }

  getScrollDelta(): number {
    const d = this.scrollDelta;
    this.scrollDelta = 0;
    return d;
  }

  get isPointerLocked(): boolean {
    // On mobile, always consider "locked" when touch controls are active
    if (this._isMobile && this.touch.isEnabled()) return true;
    return this._isPointerLocked;
  }

  releasePointerLock(): void {
    document.exitPointerLock();
  }

  clearMouseButtons(): void {
    this.mouseButtons.clear();
  }

  dispose(): void {
    document.removeEventListener('keydown', this.onKeyDown.bind(this));
    document.removeEventListener('keyup', this.onKeyUp.bind(this));
    document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    document.removeEventListener('pointerlockchange', this.onPointerLockChange.bind(this));
    this.touch.dispose();
  }
}
